import React, { useState, useMemo, useEffect } from "react";
import { Select } from "../../../shared/Select/Select";
import { SearchBar } from "../../../shared/SearchBar/SearchBar";
import { Tag } from "../Tag/Tag";
import { Button } from "../../../shared/Button/Button";
import { DateRangePickerButton } from "../../../shared/DateRangePickerButton/DateRangePickerButton";
import styles from "./FilterOrderEventNew.module.css";
import { nanoid } from "nanoid";
import classNames from "classnames";

/**
 * A component that can be used to filter events by rulesets
 * and event name. With filter items selected, clicking the filter button
 * will display selected tags underneath. Clicking the "X" will remove the
 * associated tag.
 */

export function FilterOrderEventNew({
  handleFilter,
  venueOptions,
  eventNameOptions,
  rulesetOptions,
  onRulesetChange,
  clearFilters,
  setClearFilters,
}) {
  const [rulesetStatus, setRulesetStatus] = useState("Select");
  const [venueStatus, setVenueStatus] = useState("Select");
  const [eventNameStatus, setEventNameStatus] = useState("Select");
  const [startDate, setStartDate] = useState(false);
  const [endDate, setEndDate] = useState(false);
  const [search, setSearch] = useState("");
  const [showTags, setShowTags] = useState(false);
  const [tagArray, setTagArray] = useState({});
  const [buttonStatus, setButtonStatus] = useState(false);

  useEffect(() => {
    if (clearFilters) {
      setRulesetStatus("Select");
      setVenueStatus("Select");
      setEventNameStatus("Select");
      setStartDate(false);
      setEndDate(false);
      setSearch("");
      setSearchKey(id);
      setTagArray({});
      setShowTags(false);
      setButtonStatus(false);
      handleFilter({});
      if (onRulesetChange) onRulesetChange(null);
      setClearFilters(false); // Reset the clearFilters flag
    }
  }, [clearFilters, handleFilter, onRulesetChange, setClearFilters]);

  // Ensure "Select" is always an option
  const extendedRulesetOptions = ["Select", ...rulesetOptions];
  const extendedVenueOptions = ["Select", ...venueOptions];

  const getFilteredEventNameOptions = () => {
    return ["Select", ...eventNameOptions];
  };

  const [filteredEventNameOptions, setFilteredEventNameOptions] = useState(
    getFilteredEventNameOptions
  );

  useEffect(() => {
    setFilteredEventNameOptions(getFilteredEventNameOptions());
  }, [rulesetStatus, eventNameOptions]);

  // tag array data
  useMemo(() => {}, [tagArray]);

  // re-renders daterangepicker & search bar to reset without modifying components
  const id = nanoid();
  const [dateKey, setDateKey] = useState(id);
  const [searchKey, setSearchKey] = useState(id);

  // add selection to array of tags if valid
  const onFilter = () => {
    setTagArray([]);
    const newObj = {};
    rulesetStatus !== "Select" && (newObj.ruleset = rulesetStatus);
    eventNameStatus !== "Select" && (newObj.eventName = eventNameStatus);
    venueStatus !== "Select" && (newObj.venue = venueStatus);
    startDate && (newObj.startDate = startDate);
    startDate !== endDate && endDate && (newObj.endDate = endDate);
    search && (newObj.search = search);
    setTagArray(newObj);
    setShowTags(true);
    handleFilter(newObj);
  };

  // delete tag that corresponds to x clicked
  const onDelete = (option) => {
    if (option === "ruleset") {
      setRulesetStatus("Select");
      if (onRulesetChange) onRulesetChange(null);
    } else if (option === "eventName") {
      setEventNameStatus("Select");
    } else if (option === "venue") {
      setVenueStatus("Select");
    } else if (option === "startDate" || option === "endDate") {
      setStartDate("");
      setEndDate("");
      setDateKey(id);
    } else if (option === "search") {
      setSearch("");
      setSearchKey(id);
    }

    let newObj = { ...tagArray };
    if (option === "startDate" || option === "endDate") {
      delete newObj.startDate;
      delete newObj.endDate;
    } else {
      delete newObj[option];
    }
    setTagArray(newObj);
    setShowTags(Object.keys(newObj).length > 0);
    handleFilter(newObj);
  };

  useEffect(() => {
    handleFilter(tagArray);
  }, [tagArray]);

  return (
    <div className={styles.container}>
      <div className={styles.menuRow}>
        <div className={styles.selectDiv}>
          <label className={styles.label}>Ruleset</label>
          <div className={styles.selectSpan}>
            <Select
              options={extendedRulesetOptions}
              placeholder={rulesetStatus}
              onChange={(option) => {
                const nextRuleset = option === "Select" ? null : option;
                const rulesetChanged = option !== rulesetStatus;
                setRulesetStatus(option);
                if (rulesetChanged) {
                  setEventNameStatus("Select");
                }
                setButtonStatus(true);
                if (onRulesetChange) onRulesetChange(nextRuleset);
              }}
            />
          </div>
        </div>
        <div className={styles.selectDiv}>
          <label className={styles.label}>Event Name</label>
          <div className={styles.selectSpan}>
            <Select
              className={styles.customSelect}
              options={filteredEventNameOptions}
              placeholder={eventNameStatus}
              onChange={(option) => {
                setEventNameStatus(option);
                setButtonStatus(true);
              }}
            />
          </div>
        </div>
        <div className={styles.selectDiv}>
          <label className={styles.label}>Location</label>
          <div className={styles.selectSpan}>
            <Select
              options={extendedVenueOptions}
              placeholder={venueStatus}
              onChange={(option) => {
                setVenueStatus(option);
                setButtonStatus(true);
              }}
            />
          </div>
        </div>

        <div className={styles.selectDiv}>
          <label className={classNames(styles.label)}>Date Range</label>
          <div className={styles.selectSpan}>
            <DateRangePickerButton
              key={dateKey}
              startDateString={startDate || undefined}
              endDateString={endDate || undefined}
              setStartDate={(e) => {
                setStartDate(e);
                if (startDate || endDate || e) {
                  setButtonStatus(true);
                }
              }}
              setEndDate={(e) => {
                setEndDate(e);
                if (startDate || endDate || e) {
                  setButtonStatus(true);
                }
              }}
            />
          </div>
        </div>
        <div className={styles.selectDiv}>
          <label className={classNames(styles.label)}>Search</label>
          <div className={classNames(styles.searchBar, styles.selectSpan)}>
            <SearchBar
              key={searchKey}
              placeholderText="(Search by…)"
              onInput={(value) => {
                setSearch(value);
                search !== "" ||
                rulesetStatus !== "Select" ||
                venueStatus !== "Select" ||
                eventNameStatus !== "Select" ||
                startDate !== ""
                  ? setButtonStatus(true)
                  : setButtonStatus(false);
              }}
            />
          </div>
        </div>
        <div className={styles.button}>
          <Button
            className={styles[buttonStatus ? "btnEnabled" : "btnDisabled"]}
            children="FILTER"
            onClick={onFilter}
          />
        </div>
      </div>
      <div className={styles.tagRow}>
        {showTags &&
          Object.entries(tagArray).map(([key, value], index) => (
            <div key={index} className={styles.tag}>
              <Tag
                title={value.toString()}
                isBtnLight={true}
                onClick={() => onDelete(key)}
              />
            </div>
          ))}
      </div>
    </div>
  );
}
